import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RootState, useAppSelector } from '../store';
import { Group, Model, Prompt, PromptFunction, User, UserSettings } from '../Types';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { groupService } from '../Services/groupService';
import { userService } from '../Services/userService';

const useGroup = () => {
    const navigate = useNavigate();
    const userSettings = useAppSelector((state: RootState) => state.userSettings as UserSettings);
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const demo = useAppSelector((state: RootState) => state.environment.demoInstance as boolean);
    const { groupIdentifier = '' } = useParams() || '';
    const [group, setGroup] = useState({} as Group);
    const [prompts, setPrompts] = useState<Prompt[]>([]);
    const [selectedPromptIdentifiers, setSelectedPromptIdentifiers] = useState<string[]>([]);
    const [maxCosts, setMaxCosts] = useState<number>(0.0);
    const [title, setTitle] = useState<string>(group.title);
    const [selectedModelIdentifiers, setSelectedModelIdentifiers] = useState<string[]>([]);
    const [models, setModels] = useState<Model[]>([]);
    const [functions, setFunctions] = useState<PromptFunction[]>([]);
    const [selectedFunctionIdentifiers, setSelectedFunctionIdentifiers] = useState<string[]>([]);
    const [groupLoaded, setGroupLoaded] = useState<boolean>(false);
    const [users] = useState<User[]>([]);
    const [usersLoaded, setUsersLoaded] = useState<boolean>(false);

    const notifySuccess = useCallback(() => {
        toast.success(i18n.translate({ id: 'saveGroup', sourceName: 'Group' }));
    }, [i18n]);

    const getUsersByGroupWrapper = (page: number, itemsPerPage: number, optionalParams?: Record<string, string>, query?: string|null) => {
        return userService.getUsersByGroup(page, itemsPerPage, groupIdentifier, query);
    };

    const handleSave = useCallback(() => {
        if (demo) {
            notifySuccess();
            return;
        }

        if (!userSettings.isLoggedIn) return;

        const data = new URLSearchParams();
        data.append('title', title);
        data.append('maxCosts', maxCosts.toString());
        (Array.isArray(selectedPromptIdentifiers) ? selectedPromptIdentifiers : []).forEach((prompt) => {
            data.append('prompts[]', prompt);
        });
        (Array.isArray(selectedModelIdentifiers) ? selectedModelIdentifiers : []).forEach((model) => {
            data.append('models[]', model);
        });
        (Array.isArray(selectedFunctionIdentifiers) ? selectedFunctionIdentifiers : []).forEach((func) => {
            data.append('functions[]', func);
        });

        let type = 'update/' + groupIdentifier;
        if (groupIdentifier === 'new') {
            type = 'create';
        }

        groupService.saveGroup(type, data).then((response) => {
            setGroup(response.data);
            notifySuccess();
            groupIdentifier === 'new' && navigate('/admin/groups/' + response.data.identifier);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    }, [
        demo,
        groupIdentifier,
        maxCosts,
        selectedModelIdentifiers,
        selectedPromptIdentifiers,
        selectedFunctionIdentifiers,
        title,
        userSettings,
        notifySuccess,
        navigate,
    ]);

    const handleMaxCostChange = useCallback((value: number) => {
        const newMaxCosts = value;
        setMaxCosts(newMaxCosts);
    }, []);

    const handleTitleChange = useCallback((value: string) => {
        setTitle(value);
    }, []);

    const handlePromptCheckboxChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const current = Array.isArray(selectedPromptIdentifiers) ? selectedPromptIdentifiers : [];
            const newSelectedPromptIdentifiers = event.target.checked
                ? [...current, event.target.name]
                : current.filter((id) => id !== event.target.name);

            setSelectedPromptIdentifiers(newSelectedPromptIdentifiers);
        },
        [selectedPromptIdentifiers],
    );

    const handleModelCheckboxChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const current = Array.isArray(selectedModelIdentifiers) ? selectedModelIdentifiers : [];
            const newSelectedModelIdentifiers = event.target.checked
                ? [...current, event.target.name]
                : current.filter((id) => id !== event.target.name);

            setSelectedModelIdentifiers(newSelectedModelIdentifiers);
        },
        [selectedModelIdentifiers],
    );

    const handleFunctionCheckboxChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const current = Array.isArray(selectedFunctionIdentifiers) ? selectedFunctionIdentifiers : [];
            const newSelectedFunctionIdentifiers = event.target.checked
                ? [...current, event.target.name]
                : current.filter((id) => id !== event.target.name);

            setSelectedFunctionIdentifiers(newSelectedFunctionIdentifiers);
        },
        [selectedFunctionIdentifiers],
    );

    useEffect(() => {
        if (!userSettings.isLoggedIn) return;

        const fetchData = async () => {
            try {
                const [groupResponse, promptsResponse, modelsResponse, functionsResponse] = await Promise.all([
                    groupIdentifier !== 'new' ? groupService.getGroup(groupIdentifier) : null,
                    groupService.getPrompts(),
                    groupService.getModels(),
                    groupService.getFunctions(),
                ]);

                if (groupResponse) {
                    setGroup(groupResponse.data);
                    setTitle(groupResponse.data.title ?? '');
                    setMaxCosts(parseFloat(groupResponse.data.maxCosts ?? '0'));
                    setSelectedPromptIdentifiers(Array.isArray(groupResponse.data.prompts) ? groupResponse.data.prompts : []);
                    setSelectedModelIdentifiers(Array.isArray(groupResponse.data.models) ? groupResponse.data.models : []);
                    setSelectedFunctionIdentifiers(Array.isArray(groupResponse.data.functions) ? groupResponse.data.functions : []);
                }

                setPrompts(promptsResponse.data);
                setModels(modelsResponse.data);
                setFunctions(functionsResponse.data);
                setGroupLoaded(true);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        const fetchUsers = async () => {
            setUsersLoaded(true);
        };

        fetchData();
        fetchUsers();
    }, [demo, groupIdentifier, userSettings.isLoggedIn]);

    return {
        GroupActions: {
            handleSave,
            handleMaxCostChange,
            handleTitleChange,
            handlePromptCheckboxChange,
            handleModelCheckboxChange,
            handleFunctionCheckboxChange,
            getUsersByGroupWrapper,
        },
        groupIdentifier,
        group,
        prompts,
        selectedPromptIdentifiers,
        maxCosts,
        title,
        selectedModelIdentifiers,
        models,
        functions,
        selectedFunctionIdentifiers,
        groupLoaded,
        i18n,
        usersLoaded,
        users,
        demo,
        handleNavigate: (path: string) => navigate(path),
    };
};

export default useGroup;
